// ===================================
// PROGRESS PAGE
// src/pages/Progress.js
// ===================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessions, getSessionStats } from '../services/sessionsApi';
import './Progress.css';

const Progress = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    avgScore: 0,
    practiceTime: 0
  });
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load stats
      try {
        const statsData = await getSessionStats();
        setStats({
          total: statsData?.total || 0,
          completed: statsData?.completed || 0,
          avgScore: statsData?.avgScore || 0,
          practiceTime: statsData?.practiceTime || 0
        });
      } catch (err) {
        console.error('Error loading stats:', err);
      }

      // Load completed sessions for score history
      try {
        const sessionsData = await getSessions({ status: 'completed' });
        const completedSessions = (Array.isArray(sessionsData) ? sessionsData : [])
          .filter(s => s.status === 'completed' && (s.overall_score || s.score?.overall))
          .slice(0, 10);
        setSessions(completedSessions);
      } catch (err) {
        console.error('Error loading sessions:', err);
        setSessions([]);
      }

    } catch (err) {
      console.error('Progress error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'average';
    return 'poor';
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Calculate improvement
  const getImprovement = () => {
    if (sessions.length < 2) return null;
    const recent = sessions.slice(0, 3);
    const older = sessions.slice(-3);
    
    const recentAvg = recent.reduce((sum, s) => sum + (s.overall_score || s.score?.overall || 0), 0) / recent.length;
    const olderAvg = older.reduce((sum, s) => sum + (s.overall_score || s.score?.overall || 0), 0) / older.length;
    
    return Math.round(recentAvg - olderAvg);
  };

  const improvement = getImprovement();

  if (loading) {
    return (
      <div className="progress-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="progress-page">
      <div className="progress-container">
        {/* Header */}
        <div className="page-header">
          <h1>Your Progress</h1>
          <p>Track your interview practice improvement over time</p>
        </div>

        {/* Stats Grid */}
        <div className="progress-stats">
          <div className="progress-stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">Sessions Completed</div>
          </div>
          
          <div className="progress-stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-value">{stats.avgScore || '-'}</div>
            <div className="stat-label">Average Score</div>
          </div>
          
          <div className="progress-stat-card">
            <div className="stat-icon">{improvement !== null && improvement >= 0 ? '📈' : '📉'}</div>
            <div className={`stat-value ${improvement !== null && improvement >= 0 ? 'positive' : 'negative'}`}>
              {improvement !== null ? `${improvement >= 0 ? '+' : ''}${improvement}%` : '-'}
            </div>
            <div className="stat-label">Improvement</div>
          </div>
          
          <div className="progress-stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-value">{formatTime(stats.practiceTime)}</div>
            <div className="stat-label">Total Practice</div>
          </div>
        </div>

        {/* Score Trend Chart */}
        <div className="score-trend-card">
          <h2>Score Trend</h2>
          
          {sessions.length === 0 ? (
            <div className="empty-chart">
              <p>Complete some interviews to see your score trend!</p>
            </div>
          ) : (
            <div className="score-chart">
              <div className="chart-bars">
                {[...sessions].reverse().map((session, index) => {
                  const score = session.overall_score || session.score?.overall || 0;
                  return (
                    <div key={session.id} className="chart-bar-container">
                      <div 
                        className={`chart-bar ${getScoreClass(score)}`}
                        style={{ height: `${score}%` }}
                      >
                        <span className="bar-value">{score}</span>
                      </div>
                      <span className="bar-label">#{index + 1}</span>
                    </div>
                  );
                })}
              </div>
              <div className="chart-baseline"></div>
            </div>
          )}
        </div>

        {/* Session History */}
        <div className="session-history-card">
          <h2>Recent Completed Sessions</h2>
          
          {sessions.length === 0 ? (
            <div className="empty-history">
              <p>No completed sessions yet. Start practicing!</p>
            </div>
          ) : (
            <div className="history-list">
              {sessions.map((session) => {
                const score = session.overall_score || session.score?.overall || 0;
                return (
                  <div 
                    key={session.id}
                    className="history-item"
                    onClick={() => navigate(`/sessions/${session.id}`)}
                  >
                    <div className="history-info">
                      <span className="history-type">
                        {session.interviewer_personality} • {session.experience_level}
                      </span>
                      <span className="history-date">
                        {new Date(session.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className={`history-score ${getScoreClass(score)}`}>
                      {score}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Progress;